'use client';

import { useEventListener } from 'ahooks';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { ProductLineImage } from '@/app/components/icons/ProductLineImage';
import type { TextPromptWithInputItem } from '@/app/types';
import type { HeroToolType, LocalImage } from '../types';
import { HeroInputForm } from './HeroSection/ImmersiveHero/components/HeroInputForm';

interface StickyToolInputBarProps {
  toolType: HeroToolType;
  textPrompt: TextPromptWithInputItem;
  referenceImages: LocalImage[];
  onChangePrompt: (value: TextPromptWithInputItem) => void;
  onCreate: () => void;
  onImageUpload: (file: File) => void;
  onRemoveImage: (index: number) => void;
}

export function StickyToolInputBar({
  toolType,
  textPrompt,
  referenceImages,
  onChangePrompt,
  onCreate,
  onImageUpload,
  onRemoveImage,
}: StickyToolInputBarProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setVisible(window.scrollY > window.innerHeight);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEventListener(
    'scroll',
    () => {
      if (isExpanded) setIsExpanded(false);
    },
    { target: typeof window !== 'undefined' ? window : undefined },
  );

  return (
    <div className="sm:hidden">
      <AnimatePresence>
        {visible && !isExpanded && (
          <motion.button
            type="button"
            onClick={() => setIsExpanded(true)}
            initial={{ opacity: 0, scale: 0.5, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.88 }}
            transition={{ duration: 0.5, ease: [0.15, 0.75, 0.3, 1] }}
            className="fixed bottom-10 left-1/2 z-20 flex h-16 w-[44vw] items-center gap-3 rounded-16 border border-system-white08 bg-[rgba(44,44,45,0.70)] px-4 shadow-[0_20px_100px_0_rgba(0,0,0,0.64)] backdrop-blur-[100px] hover:text-white"
            style={{ x: '-50%' }}
          >
            <ProductLineImage className="size-6" />
            <span className="text-sm text-white/30">Start with an idea or task.</span>
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {visible && isExpanded && (
          <motion.div
            initial={{ opacity: 0, scale: 0.88 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.88 }}
            transition={{ duration: 0.5, ease: [0.15, 0.75, 0.3, 1] }}
            className="fixed bottom-10 left-1/2 z-20 w-[50vw]"
            style={{ x: '-50%' }}
          >
            <HeroInputForm
              textPrompt={textPrompt}
              referenceImages={referenceImages}
              toolType={toolType}
              onCreate={() => {
                onCreate();
                setIsExpanded(false);
              }}
              onChangePrompt={onChangePrompt}
              onImageUpload={onImageUpload}
              onRemoveImage={onRemoveImage}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
